/**
 * Opt-in slab recolouring. Only elements marked `class="door-colour"` and/or
 * `data-colour-role="door"` receive the selected door HEX.
 * Never walk all paths/rects/polygons. Never recolour glazing guides, hardware
 * guides, glass slots, highlights, or shadows.
 */

const DOOR_COLOUR_SELECTOR = '.door-colour, [data-colour-role="door"]';

function hasClass(element: Element, className: string): boolean {
  return (element.getAttribute('class') ?? '')
    .split(/\s+/)
    .includes(className);
}

function isExcludedFromDoorColour(element: Element): boolean {
  if (element.closest('#glazing-guides')) return true;
  if (element.closest('#hardware-guides')) return true;
  if (hasClass(element, 'glazing-guide') || hasClass(element, 'glass-slot')) return true;
  if (hasClass(element, 'hardware-anchor') || hasClass(element, 'hardware-zone')) return true;
  const role = element.getAttribute('data-colour-role');
  return role === 'glass' || role === 'highlight' || role === 'shadow';
}

function innerMarkup(root: Element): string {
  return Array.from(root.childNodes)
    .filter((node) => node.nodeType === 1)
    .map((node) => (node as Element).outerHTML)
    .join('');
}

function wrapFragment(markup: string): string {
  return `<svg xmlns="http://www.w3.org/2000/svg">${markup}</svg>`;
}

/**
 * Apply the existing palette HEX as a flat fill on marked door-colour geometry.
 * Does not inject a slab-wide gradient or rewrite highlight/shadow paints.
 */
export function applyDoorColourToSlabArtwork(markup: string, hex: string): string {
  const document = new DOMParser().parseFromString(wrapFragment(markup), 'image/svg+xml');
  if (document.querySelector('parsererror')) return markup;
  const root = document.documentElement;

  root.querySelectorAll(DOOR_COLOUR_SELECTOR).forEach((element) => {
    if (isExcludedFromDoorColour(element)) return;
    const fill = element.getAttribute('fill');
    if (fill && fill.trim().toLowerCase() === 'none') return;
    element.setAttribute('fill', hex);
  });

  return innerMarkup(root);
}

/** @deprecated Use applyDoorColourToSlabArtwork. Kept as a named alias. */
export const applySlabTonePalette = applyDoorColourToSlabArtwork;
